import { type ClientDecisionsDto, type ProjectDto, titleCase } from '@nsb/core';
import { useApi } from '../../api.ts';
import { chosenWords } from '../../components/decisionWords.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { LoadError, Loading, Page, Title } from '../../components/ui.tsx';
import { ProjectCrumb } from './common.tsx';
import { useProject, useProjectId } from './hooks.ts';
import { RemindedYoursRow, YoursRow } from './YoursRow.tsx';
import { dateWords } from '../../days.ts';

/** A day the client reads: Oct 6, 2026. */
const day = (d: string) => dateWords(d, 'date');

/**
 * Decisions: the client's own steps of the process (Still to decide, A), for the client and for NSB alike. The old
 * template's decisions were stripped out with the estimate (2026-10-07); NSB reads the same list the client does, and
 * can mark one decided for them.
 */
export function Decisions() {
  const projectId = useProjectId();
  const { data: project, error } = useProject(projectId);
  if (error) return <LoadError error={error} />;
  if (!project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  return <ClientSteps project={project} />;
}

/**
 * The client's decisions (Still to decide, A; Jason, 2026-10-06: a step the client does or decides "would show up on
 * the waiting on you tab and the decisions list in the client view when it is reached by the work flow"): their own
 * steps of the process, waiting on them now, coming up (greyed, not reached yet), and made, each with when, what was
 * chosen where it was a choice (design study 22) and what they said. Read from `GET /process/yours`, core `clientSteps`.
 */
function ClientSteps({ project }: { project: ProjectDto }) {
  const projectId = project.id;
  const client = project.viewerRole === 'client';
  const { data, error } = useApi<ClientDecisionsDto>(['project', projectId, 'yours'], `/projects/${projectId}/process/yours`);
  useFeedbackRecord(projectId, { type: 'decisions', id: projectId, label: `${project.name} — ${client ? 'your decisions' : 'decisions'}` });
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  return (
    <Page>
      <ProjectCrumb project={project} />
      <Title>{client ? 'Your decisions' : 'Decisions'}</Title>
      {!data.shown ? (
        <p className="max-w-measure text-ink-2">Nothing here yet: NSB shares the project’s process with you when it is ready.</p>
      ) : (
        <>
          <p className="-mt-2 mb-8 max-w-measure text-ink-2">
            {client
              ? 'The steps of your project that are yours to decide or do: what waits on you now, what is coming, and what you have done.'
              : 'The steps of the project that are the client’s to decide or do, as they see them: what waits on them now, what is coming, and what they have done. Mark one decided for them when they tell you.'}
          </p>
          <section aria-labelledby="yours-waiting">
            <h2 id="yours-waiting" className="border-b border-ink/40 pb-2 text-[1.375rem]">
              {client ? 'Waiting on you' : 'Waiting on the client'}
            </h2>
            {data.waiting.length ? (
              <ul className="divide-y divide-rule border-b border-rule">
                {data.waiting.map((y) => (
                  client ? (
                    <YoursRow key={y.id} projectId={projectId} step={y} preview={false} />
                  ) : (
                    <RemindedYoursRow key={y.id} projectId={projectId} step={y} preview={false} />
                  )
                ))}
              </ul>
            ) : (
              <p className="py-4 text-ink-3">{client ? 'Nothing waits on you now.' : 'Nothing waits on the client now.'}</p>
            )}
          </section>
          <section aria-labelledby="yours-coming" className="mt-10">
            <h2 id="yours-coming" className="border-b border-ink/40 pb-2 text-[1.375rem]">
              Coming up
            </h2>
            {data.coming.length ? (
              <ul className="divide-y divide-rule border-b border-rule">
                {data.coming.map((y) => (
                  <li key={y.id} className="py-3.5 text-ink-3">
                    <p className="flex items-baseline justify-between gap-4">
                      <span>{titleCase(y.title)}</span>
                      <span className="meta shrink-0">Not reached yet</span>
                    </p>
                    {y.why ? <p className="mt-1 max-w-measure text-[0.9375rem]">{y.why}</p> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-4 text-ink-3">{client ? 'Nothing else of yours is coming up.' : 'Nothing else of theirs is coming up.'}</p>
            )}
          </section>
          <section aria-labelledby="yours-made" className="mt-10">
            <h2 id="yours-made" className="border-b border-ink/40 pb-2 text-[1.375rem]">
              Made
            </h2>
            {data.made.length ? (
              <ul className="divide-y divide-rule border-b border-rule">
                {data.made.map((y) => (
                  <li key={y.id} className="py-3.5">
                    <p className="flex items-baseline justify-between gap-4">
                      <span>{titleCase(y.title)}</span>
                      <span className="label shrink-0 !text-moss">
                        {y.kind === 'decision' ? 'Decided' : 'Done'}
                        {y.doneOn ? ` ${day(y.doneOn)}` : ''}
                      </span>
                    </p>
                    {/* What was chosen deciding it (design study 22), in the words it offered. */}
                    {y.chosen?.length ? <p className="mt-1 max-w-measure text-[0.9375rem]">Chosen: {chosenWords(y.chosen)}</p> : null}
                    {y.note ? <p className="mt-1 max-w-measure text-[0.9375rem] text-ink-2">“{y.note}”</p> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-4 text-ink-3">Nothing decided yet.</p>
            )}
          </section>
        </>
      )}
    </Page>
  );
}
